/**
 * CustomMultiMenuMobile - Mobile navigation custom element
 * Mobile: Carousel with flyout navigation
 */

class CustomMultiMenuMobile extends HTMLElement {
  #abortController = new AbortController();
  #menuData = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#loadMenuData();
    this.#render();
    this.#attachEventListeners();
    this.#applyForceHover();
  }

  disconnectedCallback() {
    console.log('CustomMultiMenuMobile disconnected from DOM');
    this.#abortController.abort();
  }

  /**
   * Load menu data from JSON script tag
   */
  #loadMenuData() {
    const scriptTag = this.querySelector('script[ref="menuData"]');
    if (!scriptTag) {
      console.error('Menu data script tag not found');
      return;
    }

    try {
      this.#menuData = JSON.parse(scriptTag.textContent || '{}');
    } catch (e) {
      console.error('Failed to parse menu data:', e);
      return;
    }
  }

  /**
   * Render menu items
   */
  #render() {
    if (!this.#menuData || !this.#menuData.menus) {
      return;
    }

    const mobileContainer = this.querySelector('.custom-multimenu-mobile__carousel');

    if (!mobileContainer) {
      return;
    }

    // Render mobile carousel buttons
    this.#menuData.menus.forEach((menu, index) => {
      const button = document.createElement('button');
      button.className = 'custom-multimenu-mobile__mobile-button';
      button.setAttribute('data-menu-index', String(index));
      button.textContent = menu.title;
      mobileContainer.appendChild(button);
    });
  }

  /**
   * Attach event listeners
   */
  #attachEventListeners() {
    const mobileButtons = Array.from(this.querySelectorAll('.custom-multimenu-mobile__mobile-button'));

    // Mobile click
    mobileButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => this.#handleMobileClick(button),
        { signal: this.#abortController.signal }
      );
    });

    // Mobile carousel drag-to-scroll
    const mobileCarousel = this.querySelector('.custom-multimenu-mobile__carousel');
    if (mobileCarousel) {
      this.#enableDragScroll(mobileCarousel);
    }

    // Listen for hamburger:open event from header
    document.addEventListener('hamburger:open', () => {
      this.#showMenuSelectionFlyout();
    }, { signal: this.#abortController.signal });
  }

  /**
   * Create footer element with links
   * @returns {HTMLElement|null}
   */
  #createFooter() {
    if (!this.#menuData || !this.#menuData.footerLinks || this.#menuData.footerLinks.length === 0) {
      return null;
    }

    const footer = document.createElement('div');
    footer.className = 'custom-multimenu-mobile__flyout-footer';

    this.#menuData.footerLinks.forEach((link) => {
      const linkEl = document.createElement('a');
      linkEl.className = 'custom-multimenu-mobile__flyout-footer-link';
      linkEl.href = link.url;
      linkEl.textContent = link.text;
      footer.appendChild(linkEl);
    });

    return footer;
  }

  /**
   * Show menu selection flyout (triggered by hamburger)
   * Displays all menu titles for selection
   */
  #showMenuSelectionFlyout() {
    if (!this.#menuData || !this.#menuData.menus) return;

    const flyoutContainer = this.querySelector('.custom-multimenu-mobile__flyouts');
    if (!flyoutContainer) return;

    // Close any existing flyout first
    flyoutContainer.innerHTML = '';

    const flyout = document.createElement('div');
    flyout.className = 'custom-multimenu-mobile__mobile-flyout';
    flyout.setAttribute('data-level', '0');

    // Header with title only (no back button at root level)
    const header = document.createElement('div');
    header.className = 'custom-multimenu-mobile__flyout-header';

    const titleEl = document.createElement('div');
    titleEl.className = 'custom-multimenu-mobile__flyout-title';
    titleEl.textContent = 'Menu';
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'custom-multimenu-mobile__flyout-close';
    closeBtn.setAttribute('aria-label', 'Close menu');
    closeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      flyoutContainer.classList.add('closing');
      setTimeout(() => {
        flyoutContainer.classList.remove('open', 'closing');
        flyoutContainer.innerHTML = '';
      }, 300);
    }, { signal: this.#abortController.signal });
    header.appendChild(closeBtn);

    flyout.appendChild(header);

    // Menu list
    const itemsList = document.createElement('div');
    itemsList.className = 'custom-multimenu-mobile__flyout-items';

    this.#menuData.menus.forEach((menu, index) => {
      const itemEl = document.createElement('div');
      itemEl.className = 'custom-multimenu-mobile__flyout-item';

      const itemLink = document.createElement('button');
      itemLink.className = 'custom-multimenu-mobile__flyout-link';

      // Check if menu has links to show
      const hasLinks = menu.links && menu.links.length > 0;
      
      if (hasLinks) {
        // If menu has only one link with no children, navigate directly
        if (menu.links.length === 1 && (!menu.links[0].children || menu.links[0].children.length === 0)) {
          itemLink.textContent = menu.title;
          itemLink.addEventListener('click', () => {
            window.location.href = menu.links[0].url;
          }, { signal: this.#abortController.signal });
        } else {
          // Show chevron and open submenu on click
          itemLink.innerHTML = `<span>${menu.title}</span><span class="custom-multimenu-mobile__flyout-chevron">›</span>`;
          itemLink.addEventListener('click', () => {
            flyout.remove();
            this.#showMobileFlyoutFromHamburger(menu, menu.title);
          }, { signal: this.#abortController.signal });
        }
      } else {
        itemLink.textContent = menu.title;
      }

      itemEl.appendChild(itemLink);
      itemsList.appendChild(itemEl);
    });

    // Add search row at the bottom
    const searchItemEl = document.createElement('div');
    searchItemEl.className = 'custom-multimenu-mobile__flyout-item custom-multimenu-mobile__flyout-item--search';

    const searchLink = document.createElement('button');
    searchLink.className = 'custom-multimenu-mobile__flyout-link custom-multimenu-mobile__flyout-link--search';
    searchLink.innerHTML = `<span class="custom-multimenu-mobile__search-icon"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg></span><span>Search</span>`;
    searchLink.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      // Close the flyout
      flyoutContainer.classList.add('closing');
      setTimeout(() => {
        flyoutContainer.classList.remove('open', 'closing');
        flyoutContainer.innerHTML = '';
      }, 300);
      // Trigger the search modal
      const searchModal = document.querySelector('#search-modal');
      if (searchModal && typeof searchModal.showDialog === 'function') {
        searchModal.showDialog();
      } else if (searchModal && searchModal.showModal) {
        searchModal.showModal();
      }
    }, { signal: this.#abortController.signal });

    searchItemEl.appendChild(searchLink);
    itemsList.appendChild(searchItemEl);

    flyout.appendChild(itemsList);

    // Add footer
    const footer = this.#createFooter();
    if (footer) flyout.appendChild(footer);

    flyoutContainer.innerHTML = '';
    flyoutContainer.appendChild(flyout);
    flyoutContainer.classList.add('open');
  }

  /**
   * Show mobile flyout from hamburger menu selection
   * @param {any} menu - Menu object
   * @param {string} title - Title for the flyout
   */
  #showMobileFlyoutFromHamburger(menu, title) {
    const flyoutContainer = this.querySelector('.custom-multimenu-mobile__flyouts');
    if (!flyoutContainer) return;

    const flyout = document.createElement('div');
    flyout.className = 'custom-multimenu-mobile__mobile-flyout';
    flyout.setAttribute('data-level', '1');

    // Header with back button, title, and close
    const header = document.createElement('div');
    header.className = 'custom-multimenu-mobile__flyout-header';

    const backBtn = document.createElement('button');
    backBtn.className = 'custom-multimenu-mobile__flyout-back';
    backBtn.setAttribute('aria-label', 'Back');
    backBtn.textContent = '‹';
    backBtn.addEventListener('click', () => {
      flyout.remove();
      this.#showMenuSelectionFlyout();
    }, { signal: this.#abortController.signal });
    header.appendChild(backBtn);

    const titleEl = document.createElement('div');
    titleEl.className = 'custom-multimenu-mobile__flyout-title';
    titleEl.textContent = title;
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'custom-multimenu-mobile__flyout-close';
    closeBtn.setAttribute('aria-label', 'Close menu');
    closeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      flyoutContainer.classList.add('closing');
      setTimeout(() => {
        flyoutContainer.classList.remove('open', 'closing');
        flyoutContainer.innerHTML = '';
      }, 300);
    }, { signal: this.#abortController.signal });
    header.appendChild(closeBtn);

    flyout.appendChild(header);

    // Items list
    const itemsList = document.createElement('div');
    itemsList.className = 'custom-multimenu-mobile__flyout-items';

    const items = menu.links || [];
    items.forEach((item) => {
      const itemEl = document.createElement('div');
      itemEl.className = 'custom-multimenu-mobile__flyout-item';

      const itemLink = document.createElement('button');
      itemLink.className = 'custom-multimenu-mobile__flyout-link';

      // If item has children, make it expandable
      if (item.children && item.children.length > 0) {
        itemLink.innerHTML = `<span>${item.title}</span><span class="custom-multimenu-mobile__flyout-chevron">›</span>`;
        itemLink.addEventListener('click', () => {
          flyout.remove();
          this.#showMobileSubFlyoutFromHamburger(item, menu, title);
        }, { signal: this.#abortController.signal });
      } else {
        // Direct link, no children
        itemLink.textContent = item.title;
        itemLink.addEventListener('click', () => {
          window.location.href = item.url;
        }, { signal: this.#abortController.signal });
      }

      itemEl.appendChild(itemLink);
      itemsList.appendChild(itemEl);
    });

    flyout.appendChild(itemsList);

    // Add footer
    const footer = this.#createFooter();
    if (footer) flyout.appendChild(footer);

    flyoutContainer.innerHTML = '';
    flyoutContainer.appendChild(flyout);
    flyoutContainer.classList.add('open');
  }

  /**
   * Show sub-flyout from hamburger navigation
   * @param {any} parentItem - Parent item with children
   * @param {any} parentMenu - Parent menu for back navigation
   * @param {string} parentTitle - Parent menu title
   */
  #showMobileSubFlyoutFromHamburger(parentItem, parentMenu, parentTitle) {
    const flyoutContainer = this.querySelector('.custom-multimenu-mobile__flyouts');
    if (!flyoutContainer) return;

    const flyout = document.createElement('div');
    flyout.className = 'custom-multimenu-mobile__mobile-flyout';
    flyout.setAttribute('data-level', '2');

    // Header with back button
    const header = document.createElement('div');
    header.className = 'custom-multimenu-mobile__flyout-header';

    const backBtn = document.createElement('button');
    backBtn.className = 'custom-multimenu-mobile__flyout-back';
    backBtn.setAttribute('aria-label', 'Back');
    backBtn.textContent = '‹';
    backBtn.addEventListener('click', () => {
      flyout.remove();
      this.#showMobileFlyoutFromHamburger(parentMenu, parentTitle);
    }, { signal: this.#abortController.signal });
    header.appendChild(backBtn);

    const titleEl = document.createElement('div');
    titleEl.className = 'custom-multimenu-mobile__flyout-title';
    titleEl.textContent = parentItem.title;
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'custom-multimenu-mobile__flyout-close';
    closeBtn.setAttribute('aria-label', 'Close menu');
    closeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      flyoutContainer.classList.add('closing');
      setTimeout(() => {
        flyoutContainer.classList.remove('open', 'closing');
        flyoutContainer.innerHTML = '';
      }, 300);
    }, { signal: this.#abortController.signal });
    header.appendChild(closeBtn);

    flyout.appendChild(header);

    // Items list
    const itemsList = document.createElement('div');
    itemsList.className = 'custom-multimenu-mobile__flyout-items';

    parentItem.children.forEach((child) => {
      const itemEl = document.createElement('div');
      itemEl.className = 'custom-multimenu-mobile__flyout-item';

      const link = document.createElement('a');
      link.className = 'custom-multimenu-mobile__flyout-link';
      link.href = child.url;
      link.textContent = child.title;
      itemEl.appendChild(link);

      itemsList.appendChild(itemEl);
    });

    flyout.appendChild(itemsList);

    // Add footer
    const footer = this.#createFooter();
    if (footer) flyout.appendChild(footer);

    flyoutContainer.innerHTML = '';
    flyoutContainer.appendChild(flyout);
    flyoutContainer.classList.add('open');
  }

  /**
   * Handle mobile button click
   * @param {HTMLElement} button
   */
  #handleMobileClick(button) {
    const index = button.getAttribute('data-menu-index');
    if (index === null || !this.#menuData) return;

    const flyoutContainer = this.querySelector('.custom-multimenu-mobile__flyouts');
    const existingFlyout = flyoutContainer.querySelector('.custom-multimenu-mobile__mobile-flyout');

    // If flyout is open and same button clicked, close it
    if (existingFlyout && button.classList.contains('active')) {
      button.classList.remove('active');
      flyoutContainer.classList.add('closing');
      setTimeout(() => {
        flyoutContainer.classList.remove('open', 'closing');
        flyoutContainer.innerHTML = '';
      }, 300);
      return;
    }

    const otherButtons = Array.from(this.querySelectorAll('.custom-multimenu-mobile__mobile-button'));
    otherButtons.forEach((btn) => btn.classList.remove('active'));
    button.classList.add('active');

    // Show flyout menu
    const menu = this.#menuData.menus[parseInt(index, 10)];
    if (menu) {
      // If menu has only one link, navigate directly instead of showing flyout
      if (menu.links && menu.links.length === 1) {
        const singleLink = menu.links[0];
        // If single link has children, show flyout for those children
        if (singleLink.children && singleLink.children.length > 0) {
          this.#showMobileFlyout(singleLink.children, singleLink.title);
        } else {
          // No children, navigate directly
          window.location.href = singleLink.url;
        }
        return;
      }
      this.#showMobileFlyout(menu, menu.title);
    }
  }

  /**
   * Show mobile flyout menu
   * @param {any} menuOrItems - Menu object or items array
   * @param {string} title - Title for the flyout
   * @param {any} currentMenu - Reference to full menu for back button
   */
  #showMobileFlyout(menuOrItems, title, currentMenu = null) {
    const flyoutContainer = this.querySelector('.custom-multimenu-mobile__flyouts');
    if (!flyoutContainer) return;

    const flyout = document.createElement('div');
    flyout.className = 'custom-multimenu-mobile__mobile-flyout';
    flyout.setAttribute('data-level', '1');

    // Header with back button and title
    const header = document.createElement('div');
    header.className = 'custom-multimenu-mobile__flyout-header';

    if (currentMenu) {
      const backBtn = document.createElement('button');
      backBtn.className = 'custom-multimenu-mobile__flyout-back';
      backBtn.setAttribute('aria-label', 'Back');
      backBtn.textContent = '‹';
      backBtn.addEventListener('click', () => {
        flyout.remove();
      }, { signal: this.#abortController.signal });
      header.appendChild(backBtn);
    }

    const titleEl = document.createElement('div');
    titleEl.className = 'custom-multimenu-mobile__flyout-title';
    titleEl.textContent = title;
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'custom-multimenu-mobile__flyout-close';
    closeBtn.setAttribute('aria-label', 'Close menu');
    closeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      const buttons = Array.from(this.querySelectorAll('.custom-multimenu-mobile__mobile-button'));
      buttons.forEach((btn) => btn.classList.remove('active'));
      flyoutContainer.classList.add('closing');
      setTimeout(() => {
        flyoutContainer.classList.remove('open', 'closing');
        flyoutContainer.innerHTML = '';
      }, 300);
    }, { signal: this.#abortController.signal });
    header.appendChild(closeBtn);

    flyout.appendChild(header);

    // Items list
    const itemsList = document.createElement('div');
    itemsList.className = 'custom-multimenu-mobile__flyout-items';

    const items = menuOrItems.links || menuOrItems;
    items.forEach((item) => {
      const itemEl = document.createElement('div');
      itemEl.className = 'custom-multimenu-mobile__flyout-item';

      const itemLink = document.createElement('button');
      itemLink.className = 'custom-multimenu-mobile__flyout-link';

      // If item has children, make it expandable
      if (item.children && item.children.length > 0) {
        itemLink.innerHTML = `<span>${item.title}</span><span class="custom-multimenu-mobile__flyout-chevron">›</span>`;
        itemLink.addEventListener('click', () => {
          flyout.remove();
          this.#showMobileSubFlyout(item, menuOrItems, title);
        }, { signal: this.#abortController.signal });
      } else {
        // Direct link, no children
        itemLink.textContent = item.title;
        itemLink.addEventListener('click', () => {
          window.location.href = item.url;
        }, { signal: this.#abortController.signal });
      }

      itemEl.appendChild(itemLink);
      itemsList.appendChild(itemEl);
    });

    flyout.appendChild(itemsList);

    // Add footer
    const footer = this.#createFooter();
    if (footer) flyout.appendChild(footer);

    flyoutContainer.innerHTML = '';
    flyoutContainer.appendChild(flyout);
    
    // Show the container
    flyoutContainer.classList.add('open');

    // Animation is applied via CSS
  }

  /**
   * Show sub-level flyout
   * @param {any} parentItem 
   * @param {any} parentMenu
   * @param {string} parentTitle
   */
  #showMobileSubFlyout(parentItem, parentMenu, parentTitle) {
    const flyoutContainer = this.querySelector('.custom-multimenu-mobile__flyouts');
    if (!flyoutContainer) return;

    const flyout = document.createElement('div');
    flyout.className = 'custom-multimenu-mobile__mobile-flyout';

    // Header with back button
    const header = document.createElement('div');
    header.className = 'custom-multimenu-mobile__flyout-header';

    const backBtn = document.createElement('button');
    backBtn.className = 'custom-multimenu-mobile__flyout-back';
    backBtn.setAttribute('aria-label', 'Back');
    backBtn.textContent = '‹';
    backBtn.addEventListener('click', () => {
      flyout.remove();
      this.#showMobileFlyout(parentMenu, parentTitle);
    }, { signal: this.#abortController.signal });
    header.appendChild(backBtn);

    const titleEl = document.createElement('div');
    titleEl.className = 'custom-multimenu-mobile__flyout-title';
    titleEl.textContent = parentItem.title;
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'custom-multimenu-mobile__flyout-close';
    closeBtn.setAttribute('aria-label', 'Close menu');
    closeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      const buttons = Array.from(this.querySelectorAll('.custom-multimenu-mobile__mobile-button'));
      buttons.forEach((btn) => btn.classList.remove('active'));
      flyoutContainer.classList.add('closing');
      setTimeout(() => {
        flyoutContainer.classList.remove('open', 'closing');
        flyoutContainer.innerHTML = '';
      }, 300);
    }, { signal: this.#abortController.signal });
    header.appendChild(closeBtn);

    flyout.appendChild(header);

    // Items list
    const itemsList = document.createElement('div');
    itemsList.className = 'custom-multimenu-mobile__flyout-items';

    parentItem.children.forEach((child) => {
      const itemEl = document.createElement('div');
      itemEl.className = 'custom-multimenu-mobile__flyout-item';

      const link = document.createElement('a');
      link.className = 'custom-multimenu-mobile__flyout-link';
      link.href = child.url;
      link.textContent = child.title;
      itemEl.appendChild(link);

      itemsList.appendChild(itemEl);
    });

    flyout.appendChild(itemsList);

    // Add footer
    const footer = this.#createFooter();
    if (footer) flyout.appendChild(footer);

    flyoutContainer.innerHTML = '';
    flyoutContainer.appendChild(flyout);
    
    // Ensure container is visible
    flyoutContainer.classList.add('open');
  }

  /**
   * Apply forced hover state for preview/testing
   */
  #applyForceHover() {
    const forceHover = this.getAttribute('data-force-hover');
    if (!forceHover || forceHover.trim() === '' || !this.#menuData) return;

    // Find the menu with matching handle
    const menuIndex = this.#menuData.menus.findIndex(
      (menu) => menu.handle === forceHover.trim()
    );

    if (menuIndex === -1) return;

    // Find the button and trigger click
    const button = this.querySelector(
      `.custom-multimenu-mobile__mobile-button[data-menu-index="${menuIndex}"]`
    );
    if (!button) return;

    // Trigger the click
    button.click();
  }

  /**
   * Enable drag-to-scroll on carousel
   * @param {HTMLElement} carousel
   */
  #enableDragScroll(carousel) {
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    carousel.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - carousel.offsetLeft;
      scrollLeft = carousel.scrollLeft;
      carousel.style.cursor = 'grabbing';
    }, { signal: this.#abortController.signal });

    carousel.addEventListener('mouseleave', () => {
      isDown = false;
      carousel.style.cursor = 'grab';
    }, { signal: this.#abortController.signal });

    carousel.addEventListener('mouseup', () => {
      isDown = false;
      carousel.style.cursor = 'grab';
    }, { signal: this.#abortController.signal });

    carousel.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startX) * 1.5;
      carousel.scrollLeft = scrollLeft - walk;
    }, { signal: this.#abortController.signal });

    // Touch support
    carousel.addEventListener('touchstart', (e) => {
      isDown = true;
      startX = e.touches[0].pageX - carousel.offsetLeft;
      scrollLeft = carousel.scrollLeft;
    }, { signal: this.#abortController.signal });

    carousel.addEventListener('touchend', () => {
      isDown = false;
    }, { signal: this.#abortController.signal });

    carousel.addEventListener('touchmove', (e) => {
      if (!isDown) return;
      const x = e.touches[0].pageX - carousel.offsetLeft;
      const walk = (x - startX) * 1.5;
      carousel.scrollLeft = scrollLeft - walk;
    }, { signal: this.#abortController.signal });
  }
}

console.log('Defining custom-multimenu-mobile element');
customElements.define('custom-multimenu-mobile', CustomMultiMenuMobile);
